/**
 * Payment Hooks
 * Custom hooks for payment operations integrated with session state
 * Updated to use 3D Secure authentication flow (Phase 2)
 */

import { useCallback } from 'react';
import { useSession } from '../context/SessionContext.jsx';
import {
  preauth3DSSession,
  continue3DSSession,
  get3DSStatus,
  preauthSession
} from '../api/sessionsApi.js';
import {
  capturePayment,
  voidPayment,
  clearSessionIdempotencyKeys
} from '../api/payments.js';
import { isDemoCard } from '../utils/demoPayments.js';
import {
  getActivePreauth, setActivePreauth, clearActivePreauth, isPreauthActive,
} from '../utils/preauthRegistry.js';
import { computeCreditBreakdown } from '../components/Steps/AmountPicker/computeCreditBreakdown.js';

/**
 * Driver-facing copy for a failed card authorization. The API already sends
 * generic text (C-11), but the browser never shows a server or exception
 * message here: a decline reads as a decline, anything else as a retry.
 */
const DECLINE_CODES = new Set(['DECLINED', 'TRANSACTION_DECLINED', 'MOCK_DECLINED']);
export const CARD_DECLINED_MESSAGE = 'Your card was declined. Please check the card details or use another card.';
export const CARD_UNAVAILABLE_MESSAGE = 'We could not authorize your card right now. Please try again or use another card.';
export const AUTH_UNCONFIRMED_MESSAGE = 'We could not confirm your card authorization yet. Tap Authorize to check again (your card will not be authorized twice), or Cancel.';
export const ATTEMPT_ENDED_MESSAGE = 'This charging attempt has ended. Please start again.';
export function driverPaymentMessage(code) {
  if (code === 'AUTH_UNCONFIRMED' || code === 'PAYMENT_STATUS_UNCERTAIN') return AUTH_UNCONFIRMED_MESSAGE;
  if (code === 'SESSION_CANCELLED' || code === 'SESSION_NOT_PAYABLE') return ATTEMPT_ENDED_MESSAGE;
  return DECLINE_CODES.has(code) ? CARD_DECLINED_MESSAGE : CARD_UNAVAILABLE_MESSAGE;
}

/** How long to wait for the method notification before continuing */
const METHOD_WAIT_MS = 10000;
/** How often to poll 3DS status while waiting on the bank */
const POLL_INTERVAL_MS = 1500;
/**
 * Hard ceiling on one Authorize press, every step included. Past it the
 * attempt is reconciled with the API once and then ends — the screen can
 * never stay on "Authorizing…" (2026-10-13 stall).
 */
export const AUTH_DEADLINE_MS = 150000;

// Single-flight + cancel: see utils/preauthRegistry.js.
export { isPreauthActive, abortActivePreauth, __resetPreauthForTests } from '../utils/preauthRegistry.js';

class AttemptCancelled extends Error {}

function sleep(ms, attempt) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (attempt.cancelled) return reject(new AttemptCancelled());
      if (Date.now() - started >= ms) return resolve();
      setTimeout(tick, Math.min(250, ms));
    };
    tick();
  });
}

const isAuthorizedResult = (r) => r?.status === 'authorized' || r?.payment?.status === 'authorized';

/**
 * Ask the API what became of this session's authorization, checking with the
 * payment provider (reconcile). Never throws.
 * @returns {Promise<{kind: 'authorized'|'failed'|'ended'|'pending'|'unknown', result?: object}>}
 */
async function reconcileOutcome(sessionUid) {
  const r = await get3DSStatus(sessionUid, { reconcile: true });
  if (!r?.ok) return { kind: 'unknown' };
  if (isAuthorizedResult(r)) return { kind: 'authorized', result: r };
  if (r.status === 'cancelled') return { kind: 'ended', result: r };
  if (r.status === 'failed') return { kind: 'failed', result: r };
  if (r.inProgress) return { kind: 'pending', result: r };
  return { kind: 'unknown', result: r };
}

/**
 * Wait for the bank's challenge to finish: the ACS page posts '3ds-complete'
 * to this window, and the API's status is polled at the same time (the
 * message can be lost — iframe unmounted, app backgrounded). The server's
 * status is the outcome either way; the message only shortens the wait.
 */
async function waitForChallenge(sessionUid, attempt, deadline) {
  let messaged = false;
  const handler = (event) => { if (event.data?.type === '3ds-complete') messaged = true; };
  window.addEventListener('message', handler);
  try {
    while (Date.now() < deadline) {
      const r = await get3DSStatus(sessionUid);
      if (attempt.cancelled) throw new AttemptCancelled();
      if (r?.ok) {
        if (isAuthorizedResult(r) || r.status === 'failed' || r.status === 'cancelled' || r.inProgress === false) {
          return r;
        }
      }
      await sleep(messaged ? 300 : POLL_INTERVAL_MS, attempt);
    }
    return { timedOut: true };
  } finally {
    window.removeEventListener('message', handler);
  }
}

const escapeAttr = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/** The auto-submitting form that opens the bank's (ACS) challenge page. */
function buildChallengeForm({ acsURL, cReq, threeDSSessionData }) {
  return `
      <html><body onload="document.forms[0].submit()">
        <form method="POST" action="${escapeAttr(acsURL)}">
          <input type="hidden" name="creq" value="${escapeAttr(cReq)}" />
          ${threeDSSessionData ? `<input type="hidden" name="threeDSSessionData" value="${escapeAttr(threeDSSessionData)}" />` : ''}
        </form>
      </body></html>
    `;
}

/**
 * Hook for managing payments in EV charging sessions
 */
export function usePayments() {
  const { state, update } = useSession();

  // ─────────────────────────────────────────────────────────────────────────
  // Helper: update payment state with 3DS info
  // ─────────────────────────────────────────────────────────────────────────
  const updatePayment = useCallback((fields) => {
    update({
      payment: { ...state.payment, ...fields }
    });
  }, [state, update]);

  const update3DS = useCallback((fields) => {
    update({
      threeds: { ...state.threeds, ...fields }
    });
  }, [state, update]);

  /**
   * The 3-D Secure exchange for one attempt. Returns an outcome, never a
   * pending state: { kind: 'authorized', result } | { kind: 'failed', code,
   * correlationId } | { kind: 'ended' } | { kind: 'unconfirmed' }.
   * Throws AttemptCancelled when the driver cancels.
   */
  const run3DS = useCallback(async (sessionUid, { amount, paymentMethod }, attempt) => {
    const deadline = Date.now() + AUTH_DEADLINE_MS;
    const check = () => { if (attempt.cancelled) throw new AttemptCancelled(); };

    /** A request that got no answer, or a timeout: ask, don't guess. */
    const settleUncertain = async () => {
      check();
      const o = await reconcileOutcome(sessionUid);
      check();
      if (o.kind === 'authorized') return { kind: 'authorized', result: o.result };
      if (o.kind === 'failed') return { kind: 'failed', code: o.result?.error || '3DS_FAILED', correlationId: o.result?.correlationId };
      if (o.kind === 'ended') return { kind: 'ended' };
      return { kind: 'unconfirmed' };
    };

    /** Map a refused/failed API step to an outcome. */
    const fromError = async (r, fallbackCode) => {
      if (r.uncertain) return settleUncertain();
      switch (r.error) {
        case 'ALREADY_AUTHORIZED':
        case 'AUTH_IN_PROGRESS':
          // Another request (other tab, earlier press) owns the attempt; its
          // result is the session's result.
          return settleUncertain();
        case 'PAYMENT_STATUS_UNCERTAIN':
          return { kind: 'unconfirmed' };
        case 'SESSION_CANCELLED':
        case 'SESSION_NOT_PAYABLE':
        case 'ATTEMPT_NOT_ACTIVE':
          return { kind: 'ended' };
        default:
          return { kind: 'failed', code: r.error || fallbackCode, correlationId: r.correlationId };
      }
    };

    const afterChallenge = async (challenge) => {
      update3DS({
        step: 'CHALLENGE',
        challengeHtml: buildChallengeForm(challenge),
        showChallenge: true,
      });
      const r = await waitForChallenge(sessionUid, attempt, deadline);
      if (r.timedOut) return settleUncertain();
      if (isAuthorizedResult(r)) return { kind: 'authorized', result: r };
      if (r.status === 'cancelled') return { kind: 'ended' };
      return { kind: 'failed', code: r.error || '3DS_FAILED', correlationId: r.correlationId };
    };

    check();
    const first = await preauth3DSSession(sessionUid, { amount, paymentMethod });
    check();
    if (!first.ok) return fromError(first, '3DS_PREAUTH_FAILED');
    if (first.step === 'COMPLETE' || isAuthorizedResult(first)) return { kind: 'authorized', result: first };
    if (first.step === 'CHALLENGE') return afterChallenge(first);

    // METHOD step: hidden fingerprinting iframe, then continue.
    update3DS({ step: 'METHOD', methodForm: first.methodForm, showChallenge: false });
    await sleep(Math.min(METHOD_WAIT_MS, Math.max(0, deadline - Date.now())), attempt);
    update3DS({ step: 'CONTINUE' });
    const cont = await continue3DSSession(sessionUid);
    check();
    if (!cont.ok) return fromError(cont, '3DS_CONTINUE_FAILED');
    if (cont.step === 'CHALLENGE') return afterChallenge(cont);
    if (cont.step === 'COMPLETE' || isAuthorizedResult(cont)) return { kind: 'authorized', result: cont };
    return settleUncertain();
  }, [update3DS]);

  /**
   * Pre-authorize payment before charging starts
   * Uses 3D Secure authentication for real cards
   */
  const preauth = useCallback(async ({ card, billing }) => {
    console.log('[PAYMENTS] Payment preauth starting');

    // CRITICAL: Must have session_uid before payment
    const sessionUid = state.sessionUid;
    if (!sessionUid) {
      console.error('[PAYMENTS] Cannot preauth: session_uid is required');
      return {
        success: false,
        error: 'Session not initialized. Please restart the charging flow.'
      };
    }

    // Single-flight: join the attempt already running for this session.
    const running = getActivePreauth();
    if (running && running.sessionUid === sessionUid && !running.cancelled) {
      return running.promise;
    }

    // The amount sent to the gateway is the CARD-side amount only:
    // total budget minus any applied credit. Server also recomputes
    // this from the pending_redeem_code — the FE is just avoiding an
    // obviously wrong Fiserv authorization request.
    const codeAmountCents = state.appliedCredit
      ? Math.round(Number(state.appliedCredit.amount || 0) * 100)
      : 0;
    const { cardAuthCents } = computeCreditBreakdown({
      selectedBudgetCents: state.amountCents,
      codeAmountCents,
    });
    const amount = cardAuthCents / 100; // dollars sent to Fiserv
    const priorPayment = state.payment;

    const attempt = { sessionUid, cancelled: false, promise: null };
    setActivePreauth(attempt);

    const fail = (code, correlationId) => {
      const errorMsg = driverPaymentMessage(code);
      update({
        payment: { ...priorPayment, status: 'error', error: { code, message: errorMsg, correlationId } },
        threeds: null
      });
      return { success: false, error: errorMsg, code, correlationId };
    };

    const run = async () => {
      // Update state to authorizing
      update({
        payment: { ...priorPayment, status: 'authorizing', error: null, needsReconcile: false },
        threeds: null // Clear any previous 3DS state
      });

      // Parse expiration date (MM/YY format)
      const expMatch = card.expiration?.match(/(\d{2})\/(\d{2})/);
      if (!expMatch) return fail('INVALID_EXPIRY');
      const [, expMonth, expYear] = expMatch;

      const paymentMethod = {
        card: {
          number: card.number.replace(/\s/g, ''), // Remove spaces
          expMonth,
          expYear: `20${expYear}`,
          cvv: card.cvv
        }
      };
      if (billing) paymentMethod.billing = billing;
      const cardNumber = paymentMethod.card.number;

      // ====== DEMO CARD (development / testing builds only) ======
      // isDemoCard is false in every production-mode build (see
      // utils/demoPayments.js). When it is on, the SERVER still authorizes
      // the card — through the direct pre-authorization route, meant for the
      // API's mock gateway — so a real hold exists before /start. The browser
      // never invents an authorization (C-02).
      if (isDemoCard(cardNumber)) {
        console.log('[usePayments] Demo card (dev build): server pre-authorization via the direct route');
        const demoResult = await preauthSession(sessionUid, { amount, paymentMethod });
        if (!demoResult.ok || demoResult.payment?.status !== 'authorized') {
          return fail(demoResult.error || 'PREAUTH_FAILED', demoResult.correlationId);
        }
        const payment = demoResult.payment;
        update({
          payment: {
            status: 'authorized',
            orderId: payment.orderId,
            transactionId: payment.transactionId,
            authorizationId: payment.transactionId || payment.orderId,
            authCode: payment.authCode,
            error: null,
            correlationId: demoResult.correlationId,
            capturedAmount: null,
            cardLast4: cardNumber.slice(-4),
            paymentMode: 'demo'
          },
          threeds: null
        });
        return { success: true, data: demoResult, isDemo: true };
      }

      // ====== REAL PAYMENT: 3D SECURE FLOW ======
      console.log('[usePayments] [3DS] Starting 3DS preauth for session:', sessionUid);
      const outcome = await run3DS(sessionUid, { amount, paymentMethod }, attempt);

      if (outcome.kind === 'authorized') {
        const finalResult = outcome.result;
        const payment = finalResult.payment || {};
        const authorizationId = payment.transactionId || payment.orderId;
        update({
          payment: {
            status: 'authorized',
            orderId: payment.orderId,
            transactionId: payment.transactionId,
            authorizationId,
            authCode: payment.authCode,
            error: null,
            correlationId: finalResult.correlationId,
            capturedAmount: null,
            cardLast4: payment.cardLast4 || cardNumber.slice(-4),
            paymentMode: 'real'
          },
          threeds: null
        });
        console.log('[usePayments] [3DS] Payment authorized:', {
          sessionUid, authorizationId, correlationId: finalResult.correlationId
        });
        return { success: true, data: finalResult };
      }
      if (outcome.kind === 'ended') return { ...fail('SESSION_NOT_PAYABLE'), ended: true };
      if (outcome.kind === 'unconfirmed') return { ...fail('AUTH_UNCONFIRMED'), unconfirmed: true };
      return fail(outcome.code || '3DS_FAILED', outcome.correlationId);
    };

    attempt.promise = run()
      .catch((error) => {
        if (error instanceof AttemptCancelled) {
          // The driver cancelled; the cancel flow owns the state from here.
          // Only clear the in-flight marker (the cancel may already have
          // reset the session; nothing from before the attempt is restored).
          update({ payment: { status: null, error: null }, threeds: null });
          return { success: false, cancelled: true };
        }
        // The exception text (a 3DS step, a network error, a parse error) is
        // logged here; the driver gets the generic retry copy.
        console.error('[usePayments] Payment preauth exception:', { error: error.message });
        return fail('PREAUTH_EXCEPTION');
      })
      .finally(() => clearActivePreauth(attempt));

    return attempt.promise;
  }, [state, update, run3DS]);

  /**
   * After a reload or app resume the page has no attempt running, but the
   * session may have one on the server (or one that already succeeded).
   * Ask the API (reconciling with the provider) and settle local state:
   * authorized → adopt it; finished/failed → clear "Authorizing";
   * still in progress → report it so the caller can wait or offer Cancel.
   * @returns {Promise<'authorized'|'pending'|'cleared'|'ended'|'unknown'>}
   */
  const reconcileAuthorization = useCallback(async () => {
    const sessionUid = state.sessionUid;
    if (!sessionUid || isPreauthActive(sessionUid)) return 'unknown';
    const o = await reconcileOutcome(sessionUid);
    if (o.kind === 'authorized') {
      const payment = o.result.payment || {};
      update({
        payment: {
          ...state.payment,
          status: 'authorized',
          orderId: payment.orderId,
          transactionId: payment.transactionId,
          authorizationId: payment.transactionId || payment.orderId,
          authCode: payment.authCode,
          error: null,
          needsReconcile: false,
          paymentMode: 'real'
        },
        threeds: null
      });
      return 'authorized';
    }
    if (o.kind === 'pending') return 'pending';
    if (o.kind === 'unknown') return 'unknown';
    update({
      payment: { ...state.payment, status: null, error: null, needsReconcile: false },
      threeds: null
    });
    return o.kind === 'ended' ? 'ended' : 'cleared';
  }, [state, update]);

  /**
   * Capture payment after charging completes
   */
  const capture = useCallback(async (finalAmount) => {
    const sessionId = state.sessionUid || state.stationId;
    const amount = finalAmount || (state.amountCents / 100);

    if (!sessionId) {
      console.error('[usePayments] Cannot capture: no session identifier');
      return { success: false, error: 'Session not found. Cannot capture payment.' };
    }

    if (state.payment?.status !== 'authorized') {
      console.error('[usePayments] Cannot capture: payment not authorized');
      return { success: false, error: 'Payment not authorized. Please authorize payment first.' };
    }

    update({
      payment: { ...state.payment, status: 'capturing' }
    });

    try {
      // A demo-card hold is a real server hold (see preauth), so capture
      // always goes to the server; the browser never reports "paid" itself.
      // ====== REAL PAYMENT HANDLING ======
      console.log('[usePayments] Capturing payment:', { sessionId, amount });

      const result = await capturePayment(sessionId, { amount });

      if (result.ok && result.payment?.status === 'paid') {
        update({
          payment: {
            ...state.payment,
            status: 'paid',
            capturedAmount: amount,
            error: null,
            correlationId: result.correlationId
          }
        });
        clearSessionIdempotencyKeys(sessionId);
        return { success: true, data: result };
      } else {
        const errorMsg = result.message || result.error || 'Payment capture failed';
        update({
          payment: {
            ...state.payment,
            status: 'error',
            error: { code: result.error, message: errorMsg, correlationId: result.correlationId }
          }
        });
        return { success: false, error: errorMsg, correlationId: result.correlationId };
      }
    } catch (error) {
      console.error('[usePayments] Capture error:', error);
      update({
        payment: {
          ...state.payment,
          status: 'error',
          error: { code: 'CAPTURE_EXCEPTION', message: error.message || 'An unexpected error occurred' }
        }
      });
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  }, [state, update]);

  /**
   * Void pre-authorization (if session cancelled before charging)
   */
  const voidAuth = useCallback(async (reason = 'Session cancelled by user') => {
    const sessionId = state.sessionUid || state.stationId;

    if (!sessionId) {
      console.error('[usePayments] Cannot void: no session identifier');
      return { success: false, error: 'Session not found. Cannot void payment.' };
    }

    if (state.payment?.status !== 'authorized') {
      console.warn('[usePayments] No authorization to void');
      return { success: true };
    }

    update({
      payment: { ...state.payment, status: 'voiding' }
    });

    try {
      // ====== REAL PAYMENT HANDLING ======
      console.log('[usePayments] Voiding payment:', { sessionId, reason });

      const result = await voidPayment(sessionId, { reason });

      if (result.ok) {
        update({
          payment: {
            ...state.payment,
            status: 'voided',
            error: null,
            correlationId: result.correlationId
          }
        });
        clearSessionIdempotencyKeys(sessionId);
        return { success: true, data: result };
      } else {
        const errorMsg = result.message || result.error || 'Payment void failed';
        update({
          payment: {
            ...state.payment,
            status: 'error',
            error: { code: result.error, message: errorMsg, correlationId: result.correlationId }
          }
        });
        return { success: false, error: errorMsg, correlationId: result.correlationId };
      }
    } catch (error) {
      console.error('[usePayments] Void error:', error);
      update({
        payment: {
          ...state.payment,
          status: 'error',
          error: { code: 'VOID_EXCEPTION', message: error.message || 'An unexpected error occurred' }
        }
      });
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  }, [state, update]);

  /**
   * Clear payment error state
   */
  const clearError = useCallback(() => {
    update({
      payment: {
        ...state.payment,
        error: null,
        status: state.payment?.status === 'error' ? null : state.payment?.status
      }
    });
  }, [state, update]);

  return {
    // Payment operations
    preauth,
    reconcileAuthorization,
    capture,
    voidAuth,
    clearError,

    // Payment state
    paymentStatus: state.payment?.status,
    paymentError: state.payment?.error,
    isAuthorizing: state.payment?.status === 'authorizing',
    isAuthorized: state.payment?.status === 'authorized',
    isCapturing: state.payment?.status === 'capturing',
    isPaid: state.payment?.status === 'paid',
    isVoiding: state.payment?.status === 'voiding',
    isVoided: state.payment?.status === 'voided',
    hasError: state.payment?.status === 'error',

    // 3DS state (for iframe rendering)
    threeds: state.threeds,

    // Payment details
    orderId: state.payment?.orderId,
    transactionId: state.payment?.transactionId,
    authCode: state.payment?.authCode,
    capturedAmount: state.payment?.capturedAmount,
    correlationId: state.payment?.correlationId,
  };
}
